import React, { FunctionComponent } from 'react';
import { useAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

export type GotoLinksSettingsValue = Record<string, boolean>;

type Props = {
  value: GotoLinksSettingsValue;
  onChange: (v: GotoLinksSettingsValue) => void;
  order: string[];
  onOrderChange: (v: string[]) => void;
};

export const CoachSettingsWidgetGotoLinks: FunctionComponent<Props> = ({
  value,
  onChange,
  order,
  onOrderChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);

  // Resolve the items in the (draft) order, falling back to the default order
  // when the draft is empty. Tolerates drift via getOrderedGotoLinks, so a
  // partial/stale order never breaks the list.
  const orderedItems = HelpersClientWidgets.getOrderedGotoLinks(order, value, currentPractice);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t("coach.personal_settings.userdetail_widgets.settings.goto_links.description")}
      </Typography>
    </Grid>

    {/* Unified reorder + show/hide list. Drag handle reorders; the switch
        toggles visibility. Disabled switches are module-gated (can't show). */}
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{padding: 1}}>
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // With useSortable, the OptimisticSortingPlugin reorders items by
            // mutating each sortable's `index` during dragover. By dragend the
            // source's new index reflects where it was dropped, so we splice it
            // from its initial index to its new index. We can't rely on
            // event.operation.target here — the plugin re-points it at source.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = orderedItems.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onOrderChange(next);
          }}
        >
          {orderedItems.map((item, index) => (
            <GotoLinkSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`coach.personal_settings.userdetail_widgets.settings.goto_links.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'active'}
              disabled={item.state === 'disabled'}
              onToggle={(checked) => onChange({ ...value, [item.key]: checked })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>
  </Grid>;
}

type RowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
};

const GotoLinkSortableRow: FunctionComponent<RowProps> = ({id, index, item, label, theme, checked, disabled, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  const stateColor = item.state === 'active'
    ? theme.palette.primary.main
    : item.state === 'inactive'
      ? theme.palette.secondary.main
      : theme.palette.text.disabled;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        disabled={disabled}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

// Read-only status row: one icon per link, coloured by state.
// primary = active, secondary = inactive (hidden), grey = disabled (module off).
type StatusProps = {
  value: GotoLinksSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetGotoLinksStatus: FunctionComponent<StatusProps> = ({
  value,
  order
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);

  const colorFor = (state: 'active' | 'inactive' | 'disabled'): string => {
    switch (state) {
      case 'active':   return theme.palette.primary.main;
      case 'inactive': return theme.palette.secondary.main;
      case 'disabled': return theme.palette.text.disabled;
    }
  };

  // Icons follow the saved order (falling back to the default order when none
  // is persisted), matching what the widget renders.
  const orderedItems = HelpersClientWidgets.getOrderedGotoLinks(order, value, currentPractice);

  return <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1.5,
    marginTop: 1
  }}>
    {orderedItems.map((item) => {
      const { Icon } = item;
      return <Box
        key={item.key}
        title={t(`coach.personal_settings.userdetail_widgets.settings.goto_links.items.${item.key}`)}
      >
        <Icon sx={{ fontSize: '1.1rem', color: colorFor(item.state) }} />
      </Box>;
    })}
  </Box>;
}
